import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

// Import state images
import MumbaiImage from '../../assets/mumbai_1.jpg';
import PuneImage from '../../assets/pune_1.jpg';
import DelhiImage from '../../assets/Delhi.jpg';
import BangaloreImage from '../../assets/bangalore_1.jpg';
import HyderabadImage from '../../assets/hyderabad_1.jpg';
import KolkataImage from '../../assets/kolkata_1.jpg';

interface StateBoxProps {
  city: string;
  image: string;
  onCityClick: (city: string) => void;
}

const StateBox: React.FC<StateBoxProps> = ({ city, image, onCityClick }) => {
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  const glassButton = {
    backgroundColor: isDark
      ? alpha('#ffffff', 0.08)
      : alpha('#ffffff', 0.6),
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    border: `1px solid ${
      isDark ? alpha('#ffffff', 0.18) : alpha('#ffffff', 0.35)
    }`,
    boxShadow: isDark
      ? `0 8px 30px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha('#fff', 0.12)}`
      : `0 8px 30px ${alpha('#000', 0.15)}, inset 0 1px 0 ${alpha('#fff', 0.6)}`
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: { xs: '16px', sm: '20px', md: '24px' },
        overflow: 'hidden',
        height: { xs: 150, sm: 200, md: 300 },
        minWidth: { xs: 150, sm: 'auto', md: 400 },
        width: '100%',
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-8px)' },
          boxShadow: isDark
            ? { xs: 'none', sm: `0 25px 50px ${alpha(goldColor, 0.4)}` }
            : { xs: 'none', sm: `0 25px 50px ${alpha(deepGreen, 0.3)}` },
          '& .city-button': {
            opacity: { xs: 0, sm: 1 },
            transform: { xs: 'translateY(20px)', sm: 'translateY(0)' },
            visibility: { xs: 'hidden', sm: 'visible' },
          },
          '& .state-image': {
            transform: { xs: 'none', sm: 'scale(1.1)' },
          },
          '& .image-overlay': {
            opacity: { xs: 0.7, sm: 1 },
          }
        },
      }}
      onClick={() => onCityClick(city)}
    >
      {/* Background Image */}
      <Box
        component="img"
        src={image}
        alt={city}
        className="state-image"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.7s ease',
          display: 'block',
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
        }}
      />
      
      {/* Dark Overlay - Full size of image */}
      <Box
        className="image-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, 
            transparent 30%, 
            ${isDark ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.8)'} 100%)`,
          opacity: { xs: 0.7, sm: 0.6 }, // More visible on mobile for better text readability
          transition: 'opacity 0.4s ease',
          zIndex: 1,
        }}
      />
      
      {/* City Name - Always visible on mobile, hidden on desktop except hover */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 16, sm: 24, md: 32 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          width: '90%',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontWeight: 800,
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: { xs: 'block', sm: 'none' }, // Only show on mobile
          }}
        >
          {city}
        </Typography>
      </Box>
      
      {/* Glass Button - Only shown on hover for tablet/desktop */}
      <Button
        className="city-button"
        variant="contained"
        sx={{
          position: 'absolute',
          bottom: 1,
          transform: 'translateX(-50%) translateY(20px)',
          opacity: 0,
          visibility: 'hidden',
          zIndex: 2,
          display: { xs: 'none', sm: 'flex' }, // Hidden on mobile

          // Glass effect
          ...glassButton,

          color: isDark ? goldColor : deepGreen,
          borderRadius: '20px',
          px: 4,
          py: 1.5,
          width: '100%',
          maxWidth: '100%',
          fontWeight: 800,
          fontSize: { sm: '0.9rem', md: '1.1rem' },
          textTransform: 'none',

          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',

          transition: 'all 0.3s ease',

          '&:hover': {
            transform: 'translateX(-50%) translateY(0)',
            backgroundColor: isDark
              ? alpha('#ffffff', 0.14)
              : alpha('#ffffff', 0.75),
            borderColor: isDark
              ? alpha(goldColor, 0.6)
              : alpha(deepGreen, 0.5),
            boxShadow: isDark
              ? `0 18px 45px ${alpha('#000', 0.8)},
                 0 0 0 1px ${alpha(goldColor, 0.2)}`
              : `0 18px 45px ${alpha(deepGreen, 0.25)},
                 0 0 0 1px ${alpha('#fff', 0.8)}`
          }
        }}
        onClick={(e) => {
          e.stopPropagation(); // Prevent double trigger
          onCityClick(city);
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 1,
          width: '100%',
        }}>
          <Box component="span" sx={{ 
            fontWeight: 900,
            letterSpacing: '0.5px',
            textShadow: isDark 
              ? `0 1px 2px ${alpha('#000', 0.5)}`
              : `0 1px 2px ${alpha('#fff', 0.5)}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {city}
          </Box>
          <Box component="span" sx={{
            opacity: 0.9,
            transform: 'translateX(-5px)',
            transition: 'transform 0.3s ease',
            fontWeight: 'bold',
            flexShrink: 0,
            'button:hover &': {
              transform: 'translateX(2px)'
            }
          }}>
            →
          </Box>
        </Box>
      </Button>
    </Box>
  );
};

const StateBoxesSection: React.FC = () => {
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Function to get city images
  const getCityImage = (city: string): string => {
    const imageMap: { [key: string]: string } = {
      'Pune': PuneImage,
      'Mumbai': MumbaiImage,
      'Delhi': DelhiImage,
      'Bangalore': BangaloreImage,
      'Kolkata': KolkataImage,
      'Hyderabad': HyderabadImage,
    };
    
    // Fallback to unsplash if images don't exist
    if (!imageMap[city]) {
      const fallbackImages: { [key: string]: string } = {
        'Pune': 'https://images.unsplash.com/photo-1593693399746-69f4d22835cc?w=800&auto=format&fit=crop',
        'Mumbai': 'https://images.unsplash.com/photo-1562979314-bee7453e911c?w=800&auto=format&fit=crop',
        'Delhi': 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=800&auto=format&fit=crop',
        'Bangalore': 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=800&auto=format&fit=crop',
        'Kolkata': 'https://images.unsplash.com/photo-1583417319070-4a69db38a482?w=800&auto=format&fit=crop',
        'Hyderabad': 'https://images.unsplash.com/photo-1594736797933-d011d2ef4c5e?w=800&auto=format&fit=crop',
      };
      return fallbackImages[city] || 'https://images.unsplash.com/photo-1498804103079-a6351b050096?w=800&auto=format&fit=crop';
    }
    
    return imageMap[city];
  };

  const cities = [
    'Pune',
    'Mumbai',
    'Delhi',
    'Bangalore',
    'Kolkata',
    'Hyderabad',
  ];

  const handleCityClick = (city: string) => {
    navigate(`/explore?search=${encodeURIComponent(city)}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
      <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
        <Typography
          variant="h2"
          sx={{
            ...discordFont,
            background: isDark
              ? `linear-gradient(135deg, ${goldColor} 0%, #FFFFFF 100%)`
              : `linear-gradient(135deg, ${deepGreen} 0%, ${alpha(deepGreen, 0.8)} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 3,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
            textShadow: isDark ? `0 4px 8px ${alpha('#000', 0.3)}` : `0 4px 8px ${alpha(deepGreen, 0.1)}`
          }}
        >
          Explore Cafes by City
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
            maxWidth: 700,
            mx: 'auto',
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
            mb: { xs: 4, md: 6 },
            lineHeight: 1.6
          }}
        >
          Discover the best coffee spots across India's top cities. Tap any city to explore cafes.
        </Typography>
      </Box>

      <Grid 
        container 
        spacing={{ xs: 1.5, sm: 3, md: 4 }}
        justifyContent="center"
        alignItems="stretch"
        sx={{ 
          maxWidth: 1400,
          mx: 'auto'
        }}
      >
        {cities.map((city) => (
          <Grid 
  size={{ xs: 4, sm: 6, lg: 4 }}
  key={city} 
  sx={{ 
    display: 'flex',
    justifyContent: 'center',
    minWidth: { xs: 150, sm: 'auto' },
  }}
>
            <Box sx={{ 
              width: '100%',
              minWidth: { xs: 150, sm: 'auto' },
              maxWidth: { xs: '100%', sm: 350, md: 380 },
              position: 'relative',
            }}>
              <StateBox
                city={city}
                image={getCityImage(city)}
                onCityClick={handleCityClick}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      
      <Box textAlign="center" mt={{ xs: 6, md: 8 }} pt={4} sx={{ 
        borderTop: `1px solid ${isDark ? alpha(goldColor, 0.1) : alpha(deepGreen, 0.1)}`
      }}>
        <Typography
          variant="body1"
          sx={{
            color: isDark ? alpha(goldColor, 0.8) : alpha(deepGreen, 0.8),
            fontWeight: 600,
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem' },
            '& span': {
              color: isDark ? goldColor : deepGreen,
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 0.8
              }
            }
          }}
        >
          Tap any city or <span onClick={() => navigate('/explore')}>explore all locations</span> to find your perfect coffee spot
        </Typography>
      </Box>
    </Container>
  );
};

export default StateBoxesSection;